import metadataToInputMetadata from "shared-utils/utils/metadataToInputMetadata";
import { TemplateType } from "shared-utils/enums/masterTemplate";

import type {
    AeComputedOverwrite,
    ComputedOverwrite,
    ConstructedDeliverable,
    InDesignComputedOverwrite,
    PsdComputedOverwrite
} from "@/types/types";

import { useParams } from "@/composables/useParams";

function getComputedOverwrites({
    computedValues,
    masterTemplate
}: Pick<ConstructedDeliverable, "computedValues" | "masterTemplate">): ComputedOverwrite[] {
    if (masterTemplate.adType === TemplateType.AE) {
        return computedValues.map(
            ({ value, name, type, publisherPlatform, settings }): AeComputedOverwrite => ({
                value,
                name,
                type: type as AeComputedOverwrite["type"],
                isPublisherPlatform: !!publisherPlatform,
                settings
            })
        );
    }

    if (masterTemplate.adType === TemplateType.PSD) {
        return computedValues.map(
            ({ value, name, type, layer, settings }): PsdComputedOverwrite => ({
                value,
                name,
                type: type as PsdComputedOverwrite["type"],
                layer,
                settings
            })
        );
    }

    if (masterTemplate.adType === TemplateType.INDESIGN) {
        return computedValues.map(
            ({ value, name, type, settings }): InDesignComputedOverwrite => ({
                value,
                name,
                type: type as InDesignComputedOverwrite["type"],
                settings
            })
        );
    }

    return computedValues.map(({ value, name, type, settings }) => ({
        value,
        name,
        type,
        settings,
        htmlTag: "manifest" as const
    }));
}

export function usePreviewValues() {
    const { campaignId } = useParams();

    function previewValues({
        computedValues,
        masterTemplate,
        language,
        metadata,
        reportingLabel
    }: ConstructedDeliverable) {
        const computedOverwrites = getComputedOverwrites({
            computedValues,
            masterTemplate
        });

        return JSON.stringify({
            computedOverwrites,

            metadata: [
                {
                    fieldName: "language",
                    value: language
                },
                ...metadataToInputMetadata(metadata)
            ],

            masterTemplate: {
                _id: masterTemplate._id,
                persistentKey: masterTemplate.persistentKey,
                width: masterTemplate.width,
                height: masterTemplate.height,
                ...(masterTemplate.adType === TemplateType.INDESIGN && {
                    sourceDocuments: masterTemplate.sourceDocuments,
                    measurementUnit: masterTemplate.measurementUnit,
                    parentWidth: masterTemplate.parentWidth,
                    parentHeight: masterTemplate.parentHeight
                }),
                ...(masterTemplate.adType === TemplateType.INDESIGN && {
                    defaultSharedOverrides: masterTemplate.defaultSharedOverrides
                }),
                ...(masterTemplate.adType === TemplateType.AE && {
                    aeInfo: masterTemplate.aeInfo
                }),
                reportingLabel: masterTemplate.reportingLabel
            },

            previewUrl: masterTemplate.preview,
            campaignId: campaignId.value,
            reportingLabel
        });
    }

    return previewValues;
}
